import axios from "axios";
import qs from "qs";
import { ApiError } from "./schemas/common.schemas";
/**
 * Extract error message from Strapi error response
 */
function extractErrorMessage(error) {
    const response = error.response?.data;
    // Strapi error format
    if (response?.error && typeof response.error === "object") {
        const strapiError = response.error;
        if (strapiError.message) {
            return strapiError.message;
        }
    }
    // Direct message
    if (response?.message && typeof response.message === "string") {
        return response.message;
    }
    // Fallback to axios message
    return error.message || "Unknown error";
}
/**
 * Extract error code from Strapi error response
 */
function extractErrorCode(error) {
    const response = error.response?.data;
    if (response?.error && typeof response.error === "object") {
        const strapiError = response.error;
        return strapiError.name;
    }
    return undefined;
}
/**
 * Extract error details from Strapi error response
 */
function extractErrorDetails(error) {
    const response = error.response?.data;
    if (response?.error && typeof response.error === "object") {
        const strapiError = response.error;
        return strapiError.details;
    }
    return undefined;
}
/**
 * API Client class
 *
 * Provides typed HTTP methods with authentication and error handling.
 */
class ApiClient {
    constructor(config) {
        this.token = null;
        this.config = config;
        this.instance = axios.create({
            baseURL: config.baseURL,
            headers: {
                "Accept-Language": "en, es-ES, es",
                ...config.headers,
            },
            // Serialize nested params to Strapi query format
            // e.g., { filters: { user: 54 } } → filters[user]=54
            paramsSerializer: (params) => qs.stringify(params, {
                encodeValuesOnly: true, // Don't encode keys (keeps brackets readable)
                arrayFormat: "indices", // arrays become key[0]=val to keep $or/$and conditions separate
            }),
        });
        // Add request interceptor for auth token and raw response format
        this.instance.interceptors.request.use((requestConfig) => {
            if (this.token) {
                requestConfig.headers.Authorization = `Bearer ${this.token}`;
            }
            // Add _raw=1 to bypass the parse-data middleware and get direct responses
            // instead of [data, meta] tuple format
            requestConfig.params = {
                _raw: "1",
                ...requestConfig.params,
            };
            return requestConfig;
        });
        // Add response interceptor for error handling
        this.instance.interceptors.response.use((response) => response, (error) => {
            // Check for forbidden — means token is expired/invalid
            if (error.response?.status === 403) {
                this.config.onForbidden?.();
            }
            // Allow custom error processing
            if (this.config.processError) {
                throw this.config.processError(error);
            }
            // Create ApiError with details
            const message = extractErrorMessage(error);
            const status = error.response?.status || 500;
            const code = extractErrorCode(error);
            const details = extractErrorDetails(error);
            throw new ApiError(message, status, code, details);
        });
    }
    /**
     * Set the authentication token
     */
    setToken(token) {
        this.token = token;
    }
    /**
     * Get the current authentication token
     */
    getToken() {
        return this.token;
    }
    /**
     * Check if a token is set
     */
    hasToken() {
        return this.token !== null;
    }
    /**
     * GET request
     */
    async get(url, config) {
        return this.instance.get(url, config);
    }
    /**
     * POST request
     */
    async post(url, data, config) {
        return this.instance.post(url, data, config);
    }
    /**
     * PUT request
     */
    async put(url, data, config) {
        return this.instance.put(url, data, config);
    }
    /**
     * PATCH request
     */
    async patch(url, data, config) {
        return this.instance.patch(url, data, config);
    }
    /**
     * DELETE request
     */
    async delete(url, config) {
        return this.instance.delete(url, config);
    }
    /**
     * Get blob response (for file downloads)
     */
    async getBlob(url, config) {
        const response = await this.instance.get(url, {
            ...config,
            responseType: "blob",
        });
        return new Blob([response.data], { type: response.headers["content-type"] });
    }
    /**
     * Post and get blob response (for file generation)
     */
    async postBlob(url, data, config) {
        const response = await this.instance.post(url, data, {
            ...config,
            responseType: "blob",
        });
        return new Blob([response.data], { type: response.headers["content-type"] });
    }
    /**
     * Upload a file
     */
    async uploadFile(file, field) {
        const formData = new FormData();
        formData.append(field || "files", file);
        return this.instance.post("/upload", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }
    /**
     * Get raw axios instance for custom use cases
     */
    getAxiosInstance() {
        return this.instance;
    }
    /**
     * Get the base URL
     */
    getBaseURL() {
        return this.config.baseURL;
    }
}
// Singleton instance - configured by the consuming app
let apiClient = null;
/**
 * Initialize the API client singleton
 */
export const initApiClient = (config) => {
    apiClient = new ApiClient(config);
    return apiClient;
};
/**
 * Get the API client singleton
 * @throws Error if client not initialized
 */
export const getApiClient = () => {
    if (!apiClient) {
        throw new Error("API client not initialized. Call initApiClient() first.");
    }
    return apiClient;
};
/**
 * Check if the API client is initialized
 */
export const isApiClientInitialized = () => {
    return apiClient !== null;
};
/**
 * Reset the API client (useful for testing or logout)
 */
export const resetApiClient = () => {
    apiClient = null;
};
export default ApiClient;
export { ApiClient };
